import { Navigate, useSearchParams } from 'react-router';
import { Testimonial } from '@hostfully/ui-components';
import { useTranslation } from 'react-i18next';
import {
  invitationTokenParamName,
  REGISTER_TESTIMONIALS,
  subscriptionIdParamName,
  userRegistrationCompletedParamName,
} from './Register.constants';
import RegisterConfirmationForm from './RegisterConfirmationForm';
import RegisterErrorState from './RegisterErrorState';
import RegisterFormSection from './RegisterFormSection';
import useRegistrationIntent from './useRegistrationIntent';
import {
  RegisterFormSectionContainer,
  RegistrationLayoutWrapper,
  TestimonialContainer,
} from './Register.styles';
import { UserRegistrationStatus } from '@/services/register.types';
import { ViewLayoutWrapper } from '@/pages/_layouts/publicLayout/layoutDesktop/LayoutDesktop.styles';
import Loader from '@/components/loader/Loader.tsx';

const Register = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const esid = searchParams.get(subscriptionIdParamName) ?? undefined;
  const it = searchParams.get(invitationTokenParamName) ?? undefined;

  const { data, isLoading, isError } = useRegistrationIntent();

  if (!esid && !it) {
    return <Navigate to="/login" replace />;
  }

  if (isError) {
    return (
      <ViewLayoutWrapper>
        <RegisterErrorState />
      </ViewLayoutWrapper>
    );
  }

  if (!data || isLoading) {
    return (
      <ViewLayoutWrapper>
        <Loader inline />
      </ViewLayoutWrapper>
    );
  }

  if (data.registrationStatus === UserRegistrationStatus.COMPLETED) {
    return (
      <Navigate
        to={`/login?${userRegistrationCompletedParamName}=true`}
        replace
      />
    );
  }

  const intentParams = { esid, it };

  if (
    data.registrationStatus ===
    UserRegistrationStatus.PENDING_USER_ACCOUNT_CONFIRMATION
  ) {
    return (
      <RegisterConfirmationForm
        anonymizedConfirmationCodeRecipient={
          data.anonymizedConfirmationCodeRecipient!
        }
        confirmationMethod={data.confirmationMethod!}
        registrationToken={data.registrationToken}
      />
    );
  }

  return (
    <ViewLayoutWrapper>
      <RegistrationLayoutWrapper>
        <TestimonialContainer>
          <Testimonial
            articles={REGISTER_TESTIMONIALS}
            prevAriaLabel={t('common.carousel.previous')}
            nextAriaLabel={t('common.carousel.next')}
            getSlideLabel={(index) => t('common.carousel.goToSlide', { index })}
            data-testid="register-testimonial"
          />
        </TestimonialContainer>
        <RegisterFormSectionContainer>
          <RegisterFormSection
            email={data.email}
            registrationType={data.registrationType}
            registrationToken={data.registrationToken}
            intentParams={intentParams}
          />
        </RegisterFormSectionContainer>
      </RegistrationLayoutWrapper>
    </ViewLayoutWrapper>
  );
};

export default Register;
